<template>
  <div>
    <div>
      <div class="w-full flex mb-4">
        <report-date-picker v-model="dateRange" @change="getReports"/>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 grid-cols-3 gap-4">
        <stats-card :title="getTotalFor('total', 'orderTotals')"
                    :previous-title="getTotalFor('total', 'previousOrderTotals')"
                    :loading="loading"
                    class="col-span-1"
                    :small-title="$t('generic.orders')"
                    type="info"
                    icon="nc-icon nc-tap-01">
        </stats-card>

        <stats-card
          :loading="loading"
          class="col-span-1"
          :small-title="$t('reports.bestProduct')"
          type="success"
          icon="nc-icon nc-paper">
          <template v-slot:title>
            {{ getMaxFor('ordersPerProductTotals').product_name }}
            <span
              class="text-base font-medium">{{ getMaxFor('ordersPerProductTotals').total }} {{ $t('generic.orders') }}</span>
          </template>
        </stats-card>

        <stats-card
          :loading="loading"
          class="col-span-1"
          :small-title="$t('reports.bestClient')"
          type="success"
          icon="nc-icon nc-spaceship">
          <template v-slot:title>
            <span class="max-w-sm truncate">
              {{ shorten(getMaxFor('ordersPerClientTotals').client_name) }}
            </span>
            <span
              class="text-base font-medium">{{ getMaxFor('ordersPerClientTotals').total }} {{ $t('generic.orders') }}</span>
          </template>
        </stats-card>

        <stats-card :title="getTotalFor('orders_sales', 'orderTotals')"
                    :previous-title="getTotalFor('orders_sales', 'previousOrderTotals')"
                    :loading="loading"
                    class="col-span-1"
                    :small-title="$t('generic.sales')"
                    type="info"
                    icon="nc-icon nc-shop">
          <template v-slot:title>
            {{ parseFloat(getTotalFor('orders_sales', 'orderTotals')) | toCurrency }}
          </template>
        </stats-card>

      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <bar-chart class="col-span-2 bg-white overflow-hidden shadow rounded-lg mb-4 p-4"
                   :chart-data="ordersPerMonth"></bar-chart>
        <payment-types-report :job_board_id="$route.params.id"
                              :date-from="dateFrom"
                              :date-till="dateTill"
        />

        <orders-per-product-report :job_board_id="$route.params.id"
                                   :date-from="dateFrom"
                                   :date-till="dateTill"
        />
      </div>
    </div>
  </div>
</template>
<script>
import ReportDatePicker from 'src/views/admin/reports/ReportDatePicker'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import subDays from 'date-fns/subDays'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import ReportService from 'src/services/ReportService'
import StatsCard from 'src/components/Cards/StatsCard'
import PaymentTypesReport from './PaymentTypesReport'
import OrdersPerProductReport from './OrdersPerProductReport'
import BarChart from 'src/components/Charts/BarChart'
import LineChart from 'src/components/Charts/LineChart'
import ReportsWarning from 'src/views/admin/reports/ReportsWarning'
import { getDefaultRange } from '@/views/admin/reports/defaultReportRange'

export default {
  name: 'ecommerce-reports',
  components: {
    StatsCard,
    BarChart,
    LineChart,
    ReportDatePicker,
    PaymentTypesReport,
    OrdersPerProductReport,
    ReportsWarning,
  },
  data () {
    const year = new Date().getFullYear()
    const defaultRange = getDefaultRange()
    return {
      year,
      defaultRange,
      dateRange: [...defaultRange],
      previousOrderTotals: {
        results: [],
      },
      orderTotals: {
        results: [],
      },
      previousOrdersPerClientTotals: {
        results: []
      },
      ordersPerClientTotals: {
        results: []
      },
      ordersPerProductTotals: {
        results: []
      },
      loading: false,
    }
  },
  computed: {
    dateFrom () {
      return get(this.dateRange, '[0]', this.defaultRange[0])
    },
    dateTill () {
      return get(this.dateRange, '[1]', this.defaultRange[1])
    },
    previousDateFrom () {
      const from = parseISO(this.dateFrom)
      const till = parseISO(this.dateTill)
      const difference = differenceInCalendarDays(till, from)
      return format(subDays(from, difference), 'yyyy-MM-dd')
    },
    previousDateTill () {
      return this.dateFrom
    },
    orderReportParams () {
      return {
        model: 'orders',
        group: 'month',
        select: 'id,total,orders_sales,client',
        date_range: 'custom',
        date_from: this.dateFrom,
        date_till: this.dateTill,
        sort: 'date_month',
      }
    },
    previousOrderReportParams () {
      return {
        model: 'orders',
        group: 'month',
        select: 'id,total,orders_sales,client',
        date_range: 'custom',
        date_from: this.previousDateFrom,
        date_till: this.previousDateTill,
        sort: 'date_month',
      }
    },
    previousOrdersPerClientReport () {
      return {
        model: 'orders',
        group: 'clients',
        select: 'id,total,client',
        date_range: 'custom',
        date_from: this.previousDateFrom,
        date_till: this.previousDateTill,
        sort: 'date_month',
      }
    },
    ordersPerClientReport () {
      return {
        model: 'orders',
        group: 'clients',
        select: 'id,total,client',
        date_range: 'custom',
        date_from: this.dateFrom,
        date_till: this.dateTill,
        sort: 'date_month',
      }
    },
    ordersPerProductReport () {
      return {
        model: 'orders',
        group: 'products',
        select: 'id,total,product',
        date_range: 'custom',
        date_from: this.dateFrom,
        date_till: this.dateTill,
        sort: 'date_month',
      }
    },
    ordersPerMonth () {
      const sortedResults = orderBy(this.orderTotals.results, 'date_format', 'asc')
      return {
        labels: sortedResults.map(r => this.$formatDate(new Date(this.year, r.date_month - 1, 1), 'MMM')),
        datasets: [
          {
            label: this.$t('reports.ordersOverTime'),
            backgroundColor: '#0695A2',
            data: sortedResults.map(r => ({
              x: new Date(this.year, r.date_month - 1, 1),
              y: r.total
            }))
          },
        ]
      }
    },
  },
  methods: {
    getTotalFor (field, dataType) {
      let sum = 0
      this[dataType].results.forEach(item => {
        let value = item[field] || 0
        if (typeof value === 'string') {
          value = parseFloat(value)
        }
        sum += value
      })
      return sum.toFixed(0)
    },
    getMaxFor (dataType) {
      let maxItem = {total: 0}
      this[dataType].results.forEach(item => {
        if (item.total > maxItem.total) {
          maxItem = item
        }
      })
      return maxItem
    },
    shorten(str = '') {
      const maxLen = 20
      if (str.length < maxLen) {
        return str
      }
      return `${str.substring(0, maxLen)}...`
    },
    async getReports () {
      try {
        this.loading = true
        await Promise.all([
          this.getOrderReports(),
          this.getPreviousOrderReports(),
          this.getPreviousOrdersPerClientReports(),
          this.getOrdersPerClientReports(),
          this.getOrdersPerProductsReports(),
        ])
      } finally {
        this.loading = false
      }
    },
    async getOrderReports () {
      const {data} = await ReportService.get(this.orderReportParams)
      this.orderTotals = data
    },
    async getPreviousOrderReports () {
      const {data} = await ReportService.get(this.previousOrderReportParams)
      this.previousOrderTotals = data
    },
    async getOrdersPerClientReports () {
      const {data} = await ReportService.get(this.ordersPerClientReport)
      this.ordersPerClientTotals = data
    },
    async getPreviousOrdersPerClientReports () {
      const {data} = await ReportService.get(this.previousOrdersPerClientReport)
      this.previousOrdersPerClientTotals = data
    },
    async getOrdersPerProductsReports () {
      const {data} = await ReportService.get(this.ordersPerProductReport)
      this.ordersPerProductTotals = data
    }
  },
  mounted () {
    this.getReports()
  }
}
</script>
<style>
</style>
